import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2Icon, PlusIcon, SearchIcon } from 'lucide-react';
import { buttonClasses } from '../../components/ui/Button';
import { QuoteStatusBadge } from '../../components/admin/QuoteStatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDate } from '../../utils/format';
import { effectiveStatus, formatKr } from '../../utils/quote';
import { quoteStatuses } from '../../types/quote';
import type { QuoteStatus } from '../../types/quote';
type Filter = 'Alle' | QuoteStatus;
export function Quotes() {
  const {
    quotes,
    dataLoading
  } = useAdminData();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('Alle');
  const withStatus = useMemo(() => quotes.map(quote => ({
    quote,
    status: effectiveStatus(quote)
  })), [quotes]);
  const summary = useMemo(() => ({
    Kladde: withStatus.filter(item => item.status === 'Kladde').length,
    Sendt: withStatus.filter(item => item.status === 'Sendt').length,
    Accepteret: withStatus.filter(item => item.status === 'Accepteret').length,
    Afvist: withStatus.filter(item => item.status === 'Afvist').length,
    Udløbet: withStatus.filter(item => item.status === 'Udløbet').length
  }), [withStatus]);
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return withStatus.filter(({
      quote,
      status
    }) => {
      const matchesFilter = filter === 'Alle' || status === filter;
      const haystack = `${quote.quoteNumber} ${quote.customerName} ${quote.title}`.toLowerCase();
      return matchesFilter && (!term || haystack.includes(term));
    });
  }, [filter, query, withStatus]);
  const cards: {
    label: string;
    value: number;
  }[] = [{
    label: 'Kladder',
    value: summary.Kladde
  }, {
    label: 'Sendt',
    value: summary.Sendt
  }, {
    label: 'Accepteret',
    value: summary.Accepteret
  }, {
    label: 'Afvist',
    value: summary.Afvist
  }, {
    label: 'Udløbet',
    value: summary.Udløbet
  }];
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-1494" data-mp="1ioi1vx703w7u">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-1495" data-mp="1nkoy2y9lhtcl">
        <div data-id="element-1496" data-mp="1sktdsm2oik1r">
          <p className="text-sm text-ink-muted" data-id="element-1497" data-mp="1sk1sq56pk4pq">{quotes.length} tilbud i alt</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-1498" data-mp="1wm6l70lf6vas">
            Tilbud
          </h1>
        </div>
        <Link to="/admin/tilbud/ny" className={buttonClasses('primary', 'md')} data-id="element-1499" data-mp="21j5drepfqu6v">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1500" data-mp="26irpfjpm82dk" />
          Opret tilbud
        </Link>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-id="element-1501" data-mp="2bbulbyhv7nwx">
        {cards.map(card => <div key={card.label} className="rounded-card border border-line bg-white p-4" data-id="element-1502" data-mp="2gbyt904pjabj">
            <p className="font-display text-2xl font-bold tracking-tight text-ink" data-id="element-1503" data-mp="2k9o5yc69ot49">{card.value}</p>
            <p className="mt-1 text-sm text-ink-muted" data-id="element-1504" data-mp="2p8rbryu2n3da">{card.label}</p>
          </div>)}
      </div>

      <div className="mt-6" data-id="element-1505" data-mp="dl4af444xg6e">
        <label htmlFor="tilbudsoegning" className="sr-only" data-id="element-1506" data-mp="8nw35ykiywjx">
          Søg i tilbud
        </label>
        <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3" data-id="element-1507" data-mp="4maivs5nvfzp">
          <SearchIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden="true" data-id="element-1508" data-mp="3vtmyw3ut5ne1" />
          <input id="tilbudsoegning" value={query} onChange={event => setQuery(event.target.value)} placeholder="Søg på tilbudsnummer, kunde eller titel" className="w-full bg-transparent text-[15px] text-ink placeholder:text-ink-faint focus:outline-none" data-id="element-1509" data-mp="wdqeuct3yf2l" />
        </div>

        <div className="mt-4 flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Filtrér tilbud efter status" data-id="element-1510" data-mp="ilsbkim2vdec">
          {(['Alle', ...quoteStatuses] as Filter[]).map(option => {
          const active = filter === option;
          return <button key={option} type="button" onClick={() => setFilter(option)} aria-pressed={active} className={`whitespace-nowrap rounded-full border px-3.5 py-2 text-sm transition-colors duration-200 ease-smooth ${active ? 'border-accent/50 bg-accent/15 text-ink' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-1511" data-mp="uuyivdzij58k">
                {option}
              </button>;
        })}
        </div>
      </div>

      {dataLoading && quotes.length === 0 && <p className="mt-8 flex items-center gap-2 text-[15px] text-ink-muted" data-id="element-1512" data-mp="19t0mfo6jnqpt">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1513" data-mp="19qwcaoi1xvh3" />
          Henter tilbud …
        </p>}

      <div className="mt-6 rounded-card border border-line bg-white" data-id="element-1514" data-mp="2291krwq2wjo3">
        <div className="hidden md:block" data-id="element-1515" data-mp="22beawx5zq0c8">
          <table className="w-full text-left text-[15px]" data-id="element-1516" data-mp="2bnlfnw9d8ulc">
            <thead data-id="element-1517" data-mp="2bm0qkm5tm3uv">
              <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-muted" data-id="element-1518" data-mp="27mruhanb5tw5">
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1519" data-mp="2ldn8108r4z62">Tilbudsnummer</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1520" data-mp="2lfr8f87fzg1l">Kunde</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1521" data-mp="2gflneavogrsh">Titel</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1522" data-mp="1sq0squq5w63d">Dato</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1523" data-mp="1oo5qno0epooj">Gyldig til</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1524" data-mp="22nf5aj8ysaqe">Beløb</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-1525" data-mp="22lc9fil4rndr">Status</th>
                <th scope="col" className="px-5 py-3 font-medium text-right" data-id="element-1526" data-mp="1xmr8ti1yv1os">Handling</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-1527" data-mp="3e4eyri71svm0">
              {visible.map(({
              quote,
              status
            }) => <tr key={quote.id} className="transition-colors duration-200 ease-smooth hover:bg-canvas" data-id="element-1528" data-mp="3h471xii3ezhz">
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-ink" data-id="element-1529" data-mp="3l5ii6xf2u8di">{quote.quoteNumber}</td>
                  <td className="px-5 py-3.5 text-ink" data-id="element-1530" data-mp="3q4db4s6rddbc">{quote.customerName || '—'}</td>
                  <td className="px-5 py-3.5 text-ink-soft" data-id="element-1531" data-mp="3v3ydlcl503st">{quote.title}</td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-1532" data-mp="3v1vk2ikrzsjc">{formatDate(quote.quoteDate)}</td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-1533" data-mp="32fajqsmbo63n">{formatDate(quote.validUntil)}</td>
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-ink" data-id="element-1534" data-mp="37engc52zwrjf">{formatKr(quote.total)}</td>
                  <td className="px-5 py-3.5" data-id="element-1535" data-mp="3c5lx15rwbrn7">
                    <QuoteStatusBadge status={status} data-id="element-1536" data-mp="3c6ezhujtnuzj" />
                  </td>
                  <td className="px-5 py-3.5 text-right" data-id="element-1537" data-mp="2eox27w1d5pnb">
                    <Link to={`/admin/tilbud/${quote.id}`} className={buttonClasses('secondary', 'sm')} data-id="element-1538" data-mp="2jojfg5h6z5w4">
                      Åbn
                    </Link>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        <ul className="divide-y divide-line md:hidden" data-id="element-1539" data-mp="2744a85jqb3tb">
          {visible.map(({
          quote,
          status
        }) => <li key={quote.id} data-id="element-1540" data-mp="228xd5gkysxdd">
              <Link to={`/admin/tilbud/${quote.id}`} className="block px-5 py-4" data-id="element-1541" data-mp="1x8j4pxfgblet">
                <div className="flex items-start justify-between gap-3" data-id="element-1542" data-mp="1t6fs35h1qary">
                  <div className="min-w-0" data-id="element-1543" data-mp="1t8ir2f06fe8t">
                    <p className="text-xs text-ink-faint" data-id="element-1544" data-mp="1offt7uod9imt">{quote.quoteNumber}</p>
                    <p className="mt-1 truncate text-[15px] font-medium text-ink" data-id="element-1545" data-mp="1jgcyb323ossk">{quote.title}</p>
                    <p className="mt-0.5 truncate text-sm text-ink-muted" data-id="element-1546" data-mp="1ehrwx0b4432j">{quote.customerName}</p>
                  </div>
                  <QuoteStatusBadge status={status} data-id="element-1547" data-mp="19iosni2yi0vm" />
                </div>
                <p className="mt-2 text-sm text-ink-soft" data-id="element-1548" data-mp="15h3iw5lpy2pn">
                  {formatDate(quote.quoteDate)} · {formatKr(quote.total)}
                </p>
              </Link>
            </li>)}
        </ul>

        {!dataLoading && visible.length === 0 && <p className="px-5 py-12 text-center text-sm text-ink-muted" data-id="element-1549" data-mp="2ybuoqst6sxmq">Ingen tilbud matcher søgningen.</p>}
      </div>
    </div>;
}